import { ServiceTypes } from '@/enums/serviceTypes'

/**
 * Registry de servicios de viaje: fuente ÚNICA de icono, etiqueta, color, ruta y
 * permiso por servicio.
 *
 * Antes esto vivía repartido en once mapas escritos a mano (switch, objeto literal
 * y cadenas de ternarios, las tres formas), y ya habían divergido entre sí:
 *
 *  - `useServiceMedia` daba a Bus el icono `monetization_on` (un icono de dinero)
 *    mientras `StatusMessage` daba `directions_bus`. Los dos switches eran
 *    copy-paste y solo se editó uno, así que el icono equivocado se veía en la
 *    pestaña de políticas y en el resumen de CEF.
 *  - `EntryPoliciesModal` tenía las claves en español (`Vuelo`, `Auto`), así que
 *    `Plane` y `Car` nunca hacían match y caían al gris del fallback.
 *  - `NoServicesMessage` y `GeneralInformationByCef` decían "Adquiere un ingreso
 *    extra" e "Ingresos extras" para Bus, etiquetas heredadas de otro significado.
 *
 * Regla al agregar un servicio: se agrega UNA entrada aquí. Si hay que tocar otro
 * archivo para que el servicio se vea bien, es que quedó un mapa suelto por migrar.
 */
export interface ServiceDescriptor {
	/** Etiqueta es-MX para UI ("Autobús"). */
	label: string
	/** Nombre del icono de Material Symbols. */
	icon: string
	/** Clase de fondo (Tailwind) de la pantalla de confirmación. */
	backgroundImage: string
	/** Color del icono / acento. */
	accent: string
	/** Fondo del chip o del contenedor del icono. */
	bg: string
	/** Gradiente de tinte de la tarjeta de itinerario. */
	tint: string
	/** Clase de badge para la píldora de servicio. */
	badge: string
	/**
	 * Nombre de la ruta del formulario donde se busca o se solicita este servicio.
	 *
	 * Antes aquí no había `route`, y con razón: traía `/services/hotel` y
	 * `/services/plane`, que son rutas PADRE, mientras los formularios de verdad viven
	 * en `hotel.availability` y `plane.search`. Quedó anotado que entraría «cuando haya
	 * un consumidor y con las rutas reales».
	 *
	 * El consumidor ya existe: los avisos con acción `service_search` mandan al viajero
	 * al formulario del servicio de la promoción. Y ahora son NOMBRES de ruta, no rutas
	 * escritas a mano, así que un renombre lo caza el router y no el usuario.
	 */
	searchRouteName: string
	/** Ability de Spatie que habilita crear una solicitud de este servicio. */
	ability: string
	/**
	 * ¿Pasa por cotizaciones offline (ops propone, el cliente acepta o rechaza)?
	 *
	 * Hotel tiene tarjeta propia y su propio flujo; auto y autobús caen en
	 * ServiceCardGeneric y necesitan la sección de cotizaciones, si no el cliente
	 * nunca ve lo que ops cotizó y la solicitud se queda congelada.
	 */
	supportsQuotations: boolean
	/**
	 * Etiquetas de los chips de detalle de una cotización, por llave de `details`.
	 *
	 * Vive aquí porque `OfflineQuotationsSection` las tenía hardcodeadas con
	 * nombres de auto ("Categoría", "Cobertura", "Entrega"), que para un autobús
	 * son incorrectas.
	 */
	detailLabels: Record<string, string>
	/**
	 * Llamada a la acción cuando el itinerario no tiene este servicio.
	 *
	 * Vive aquí porque en `NoServicesMessage` el texto de Bus era "Adquiere un
	 * ingreso extra", heredado de otro significado del módulo.
	 */
	emptyStateCta: string
}

const HOTEL: ServiceDescriptor = {
	label: 'Hotel',
	icon: 'apartment',
	backgroundImage: 'bg-confirmation-hotel',
	accent: 'text-amber-600',
	bg: 'bg-amber-50',
	tint: 'from-amber-50/60',
	badge: 'bg-amber-100 text-amber-700',
	ability: 'create-hotel',
	searchRouteName: 'hotel.availability',
	// Hospedaje es el servicio donde nacieron las cotizaciones offline. Estaba en
	// `false` porque `ServiceCardHotel` tenía su propio gate —`suppliableId === null`—
	// y nunca preguntaba al registry; al quitarlo (ENL-310) la bandera vuelve a decir
	// la verdad. Sólo la lee la tarjeta que corresponda: hospedaje va por
	// `ServiceCardHotel` y el resto por `ServiceCardGeneric`, nunca las dos.
	supportsQuotations: true,
	// `name` y `address` NO van aquí: `OfflineQuotationsSection` ya los pinta como
	// título y subtítulo de la tarjeta, así que como chips salían dos veces.
	detailLabels: {},
	emptyStateCta: 'Haz una reservación a un hotel',
}

const PLANE: ServiceDescriptor = {
	label: 'Vuelo',
	// ⚠️ Había TRES iconos para vuelo en el repo: `travel` (useServiceMedia,
	// StatusMessage) y `flight` (PreviewRequest, ListRequest,
	// UnlinkItineraryWarningModal). Se unifica a `flight`, que es el estándar de
	// Material Symbols para un avión y el que usaban 3 de los 5 sitios. Efecto
	// visible: la pantalla de confirmación cambia de maleta a avión.
	icon: 'flight',
	backgroundImage: 'bg-confirmation-plane',
	accent: 'text-blue-600',
	bg: 'bg-blue-50',
	tint: 'from-blue-50/60',
	badge: 'bg-blue-100 text-blue-700',
	ability: 'create-plane',
	searchRouteName: 'plane.search',
	supportsQuotations: false,
	detailLabels: {},
	emptyStateCta: 'Adquiere un vuelo',
}

const CAR: ServiceDescriptor = {
	label: 'Automóvil',
	icon: 'directions_car',
	backgroundImage: 'bg-confirmation-car',
	accent: 'text-sky-600',
	bg: 'bg-sky-50',
	tint: 'from-sky-50/60',
	badge: 'bg-sky-100 text-sky-700',
	ability: 'create-car',
	searchRouteName: 'car.form',
	supportsQuotations: true,
	detailLabels: {
		carType: 'Categoría',
		insuranceType: 'Cobertura',
		pickupLocation: 'Entrega',
		dropoffLocation: 'Devolución',
		pickupTime: 'Hora entrega',
		dropoffTime: 'Hora devolución',
		pickupBranch: 'Sucursal',
		specialRequirements: 'Incluye',
	},
	emptyStateCta: 'Renta un automóvil',
}

const BUS: ServiceDescriptor = {
	label: 'Autobús',
	// Antes `useServiceMedia` daba 'monetization_on' aquí. Los otros diez mapas
	// decían 'directions_bus'; este era el divergente.
	icon: 'directions_bus',
	backgroundImage: 'bg-confirmation-bus',
	accent: 'text-violet-600',
	bg: 'bg-violet-50',
	tint: 'from-violet-50/60',
	badge: 'bg-violet-100 text-violet-700',
	ability: 'create-bus',
	searchRouteName: 'bus.form',
	supportsQuotations: true,
	detailLabels: {
		pickupLocation: 'Origen',
		dropoffLocation: 'Destino',
		pickupTime: 'Hora salida',
		dropoffTime: 'Hora regreso',
		pickupBranch: 'Terminal',
		specialRequirements: 'Incluye',
	},
	emptyStateCta: 'Reserva un autobús',
}

export const SERVICES: Record<ServiceTypes, ServiceDescriptor> = {
	[ServiceTypes.Hotel]: HOTEL,
	[ServiceTypes.Plane]: PLANE,
	[ServiceTypes.Car]: CAR,
	[ServiceTypes.Bus]: BUS,
}

/**
 * Descriptor para un servicio desconocido.
 *
 * Existe porque el catálogo de servicios lo sirve el backend (`GET /api/services`):
 * puede llegar un `serviceName` que el front todavía no conoce, y es mejor
 * renderizar algo neutro que romper la vista.
 */
export const UNKNOWN_SERVICE: ServiceDescriptor = {
	label: 'Servicio',
	icon: 'category',
	backgroundImage: 'bg-confirmation-hotel',
	accent: 'text-gray-600',
	bg: 'bg-gray-50',
	tint: 'from-gray-50/60',
	badge: 'bg-gray-100 text-gray-700',
	ability: '',
	// Sin ruta: un servicio que este front no conoce no tiene formulario a donde
	// mandar. Quien consuma esto debe comprobar que no venga vacío.
	searchRouteName: '',
	supportsQuotations: false,
	detailLabels: {},
	emptyStateCta: 'Agrega un servicio',
}

/**
 * Índice por nombre en minúsculas.
 *
 * El repo tiene el nombre del servicio en dos casings según de dónde venga: el
 * catálogo del backend manda `services.name` capitalizado (`Bus`), pero varios
 * componentes reciben la versión en minúsculas (`bus`) desde el tipo del
 * itinerario. Resolver case-insensitive evita que cada consumidor normalice —y
 * evita el fallback silencioso que ya causaba divergencias.
 */
const BY_LOWER_NAME: Record<string, ServiceDescriptor> = Object.fromEntries(
	Object.entries(SERVICES).map(([name, descriptor]) => [
		name.toLowerCase(),
		descriptor,
	])
)

/**
 * Descriptor de un servicio por su nombre (`services.name`, en cualquier casing).
 *
 * Nunca devuelve `undefined`: cae a `UNKNOWN_SERVICE` para que quien lo consuma no
 * tenga que repetir el fallback en cada plantilla.
 */
export function serviceDescriptor(
	serviceName: string | null | undefined
): ServiceDescriptor {
	if (!serviceName) return UNKNOWN_SERVICE

	return BY_LOWER_NAME[serviceName.toLowerCase()] ?? UNKNOWN_SERVICE
}

/** Servicios que pasan por cotizaciones offline. */
export function serviceSupportsQuotations(
	serviceName: string | null | undefined
): boolean {
	return serviceDescriptor(serviceName).supportsQuotations
}

/**
 * Etiqueta del chip de una llave de `details`, según el servicio.
 *
 * Devuelve `null` si el servicio no declara esa llave, para que el consumidor la
 * omita en vez de mostrar una etiqueta de otro servicio.
 */
export function serviceDetailLabel(
	serviceName: string | null | undefined,
	detailKey: string
): string | null {
	return serviceDescriptor(serviceName).detailLabels[detailKey] ?? null
}

/**
 * IDs de `public.services`, como RESPALDO de `serviceName`.
 *
 * ⚠️ Son ids de base de datos hardcodeados en el front. Estaban triplicados a
 * mano (`trips/pages/ShowPage`, `trips/components/ItinerarySummaryAlert`,
 * `hotels/components/LinkedFlightBanner`), cada copia con solo HOTEL y PLANE.
 *
 * Se centralizan aquí para quitar la triplicación sin cambiar comportamiento. Lo
 * correcto a futuro es resolverlos desde `useServices()`, que ya trae el catálogo
 * del backend — pero eso vuelve asíncrono un chequeo que hoy es síncrono, así que
 * es un cambio aparte.
 */
const SERVICE_IDS: Record<ServiceTypes, number> = {
	[ServiceTypes.Hotel]: 1,
	[ServiceTypes.Plane]: 2,
	[ServiceTypes.Car]: 3,
	[ServiceTypes.Bus]: 4,
}

/** Forma mínima que necesita `isService` para clasificar una solicitud. */
export interface ServiceIdentifiable {
	serviceId?: number | string | null
	serviceName?: string | null
}

/**
 * ¿La solicitud es de este servicio?
 *
 * Compara por nombre y, como respaldo, por id: hay payloads donde `serviceName`
 * no viene. Ese OR es el comportamiento que ya tenían las tres copias.
 *
 * El id se coacciona a número a propósito: las columnas `numeric` de Postgres
 * llegan como string vía PDO, y por eso la copia de `trips/pages/ShowPage` usaba
 * `==` con un comentario explicándolo. Aquí se hace explícito en vez de depender
 * de la comparación laxa.
 */
export function isService(
	request: ServiceIdentifiable,
	serviceType: ServiceTypes
): boolean {
	if (request.serviceName) {
		return request.serviceName.toLowerCase() === serviceType.toLowerCase()
	}

	if (request.serviceId === null || request.serviceId === undefined) {
		return false
	}

	return Number(request.serviceId) === SERVICE_IDS[serviceType]
}
